import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dailyCareLogsApi, DailyCareLogDetail as LogDetail } from '../api/dailyCareLogs';

const DailyCareLogDetail: React.FC = () => {
  const { logId } = useParams<{ logId: string }>();
  const navigate = useNavigate();
  const [log, setLog] = useState<LogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ generalNotes: '', handoverNote: '', concernFlag: false, concernNote: '' });
  const [saving, setSaving] = useState(false);

  const fetchLog = () => {
    if (!logId) return;
    setLoading(true);
    dailyCareLogsApi
      .getLog(Number(logId))
      .then(setLog)
      .catch(e => setError(e.response?.data?.message || 'Failed to load log'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logId]);

  const openEditModal = () => {
    if (!log) return;
    setEditForm({
      generalNotes: log.generalNotes || '',
      handoverNote: log.handoverNote || '',
      concernFlag: log.concernFlag ?? false,
      concernNote: log.concernNote || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!logId) return;
    setSaving(true);
    try {
      await dailyCareLogsApi.updateLog(Number(logId), {
        generalNotes: editForm.generalNotes,
        handoverNote: editForm.handoverNote,
        concernFlag: editForm.concernFlag,
        concernNote: editForm.concernNote,
      });
      setShowEditModal(false);
      fetchLog();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Failed to update log');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  if (error || !log) {
    return (
      <div>
        <button onClick={() => navigate('/daily-care-logs')} className="text-blue-600 hover:text-blue-800 text-sm mb-4">
          &larr; Back to logs
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Log not found'}
        </div>
      </div>
    );
  }

  const timeStr = log.logTime
    ? new Date(log.logTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div>
      <button
        onClick={() => navigate('/daily-care-logs')}
        className="text-blue-600 hover:text-blue-800 text-sm mb-4"
      >
        &larr; Back to logs
      </button>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="border-b border-gray-200 pb-4 mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {log.resident?.name || `Resident #${log.residentId}`}
            </h1>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
              <span>
                <strong>Date:</strong> {log.logDate}
              </span>
              <span>
                <strong>Time:</strong> {timeStr}
              </span>
              <span>
                <strong>Carer:</strong> {log.carer?.name || `#${log.carerId}`}
              </span>
            </div>
          </div>
          <button
            onClick={openEditModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            Edit This Log
          </button>
        </div>

        <div className="space-y-4">
          {log.entries.length === 0 && (
            <p className="text-gray-500 text-sm">No category entries in this log.</p>
          )}
          {log.entries.map(entry => (
            <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                {entry.category?.name || `Category #${entry.categoryId}`}
              </h3>

              {entry.selectedSubItems && entry.selectedSubItems.length > 0 && (
                <ul className="list-disc pl-5 text-sm text-gray-700 mb-2">
                  {entry.selectedSubItems.map(s => (
                    <li key={s.id}>{s.name}</li>
                  ))}
                </ul>
              )}

              {entry.extraData && Object.keys(entry.extraData).length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 mb-2">
                  {Object.entries(entry.extraData).map(([k, v]) => (
                    <span key={k} className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded">
                      <strong>{k}:</strong> {String(v)}
                    </span>
                  ))}
                </div>
              )}

              {entry.notes && (
                <p className="text-sm text-gray-600 italic">{entry.notes}</p>
              )}
            </div>
          ))}
        </div>

        {log.generalNotes && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-900 mb-2">General Notes</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.generalNotes}</p>
          </div>
        )}

        {/* Handover Note */}
        {log.handoverNote && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 mt-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Shift Handover Note</h3>
            <p className="text-sm text-blue-700">{log.handoverNote}</p>
          </div>
        )}

        {/* Concern Flag */}
        <div className={`border rounded-lg p-4 mb-4 mt-4 ${log.concernFlag ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <h3 className={`text-sm font-semibold mb-1 ${log.concernFlag ? 'text-red-800' : 'text-green-800'}`}>
            Concern Flagged: {log.concernFlag ? '\u26a0\ufe0f YES' : '\u2713 NO'}
          </h3>
          {log.concernFlag && log.concernNote && (
            <p className="text-sm text-red-700 mt-1">{log.concernNote}</p>
          )}
        </div>

        {/* Edit Audit Trail */}
        {log.editedAt && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-yellow-800">
              \ud83d\udcdd Edited by: {log.editedBy?.name || 'Admin'} | Edited at: {new Date(log.editedAt).toLocaleString()}
            </p>
          </div>
        )}

        {log.photos && log.photos.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Photos</h3>
            <div className="flex flex-wrap gap-3">
              {log.photos.map(
                p =>
                  p.url && (
                    <a key={p.key} href={p.url} target="_blank" rel="noreferrer">
                      <img
                        src={p.url}
                        alt={p.key}
                        className="w-40 h-40 object-cover rounded-md border border-gray-200 hover:opacity-90"
                      />
                    </a>
                  )
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Edit Care Log</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">General Notes</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  value={editForm.generalNotes}
                  onChange={e => setEditForm(f => ({ ...f, generalNotes: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Handover Note</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  value={editForm.handoverNote}
                  onChange={e => setEditForm(f => ({ ...f, handoverNote: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={editForm.concernFlag}
                    onChange={e => setEditForm(f => ({ ...f, concernFlag: e.target.checked }))}
                  />
                  Flag a Concern
                </label>
              </div>

              {editForm.concernFlag && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concern Note</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={2}
                    value={editForm.concernNote}
                    onChange={e => setEditForm(f => ({ ...f, concernNote: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyCareLogDetail;
